import { PaymentIntentMetadata } from "../../stripe";
import { DateTime, isWholeNumber, NumberFormatter } from "../../util";
import { AttributeOption } from "../AttributeOption";
import { Invoice } from "../Invoice";
import { PaymentPlan } from "../PaymentPlan";
import { Policy } from "../Policy/Policy";
import { PolicyBillingNotification } from "../PolicyBillingNotification";
import { Charge } from "./Charge";
import type { PolicyBilling } from "./PolicyBilling";

interface IAmount {
    charged: number;
    paid: number;
}

export abstract class BillingPeriod {
    static PAYMENT_CONSISTENCY_THRESHOLD = 0.6;

    static getBillingPeriodForMonthAndYear({
        billingPeriods,
        month,
        year,
    }: {
        billingPeriods: BillingPeriod[];
        month: number;
        year: number;
    }): BillingPeriod | null {
        const dt = new DateTime(year, month - 1, 1).last();
        return billingPeriods.find((bp) => {
            if (bp.isLast) {
                dt.first();
            }
            if (dt.isBetween(bp.startDate, bp.endDate, { excludeTime: true })) return bp;
            return null;
        });
    }

    static calculatePaymentConsistencyScore(billingPeriods: BillingPeriod[]): number {
        const billingPeriodsWithCharged = billingPeriods.filter((bp) => bp.charged > 0);
        const chargedTotals = new Map<number, number>();
        billingPeriodsWithCharged.map((bp) => {
            chargedTotals.set(bp.charged, (chargedTotals.get(bp.charged) || 0) + 1);
        });
        const totalMatches = Array.from(chargedTotals.values()).reduce((acc, val) => {
            if (val > 1) {
                return acc + val;
            }
            return acc;
        }, 0);
        return totalMatches / billingPeriodsWithCharged.length;
    }

    static isAbovePaymentConsistencyThreshold(billingPeriods: BillingPeriod[]): boolean {
        return BillingPeriod.calculatePaymentConsistencyScore(billingPeriods) >= BillingPeriod.PAYMENT_CONSISTENCY_THRESHOLD;
    }

    static calculateProjectedCharge(bp: BillingPeriod): number {
        const previousBillingPeriodsWithCharged = bp.getAllPreviousWithCharges();
        const previousBillingPeriod = bp.getPrevious();
        const totalBillingPeriodsWithProjectedCharge = bp.schedule.billingPeriods.length - (previousBillingPeriodsWithCharged?.length || 0);
        const totalCharged =
            previousBillingPeriodsWithCharged?.reduce((acc, bp) => {
                return acc + bp.charged;
            }, 0) || 0;
        const remainingPremium = bp.schedule.policy.totalCost - totalCharged;

        return BillingPeriod.calculateWholeNumberCharge(
            {
                remainingPremium,
                totalUnpaidBillingPeriods: totalBillingPeriodsWithProjectedCharge,
            },
            previousBillingPeriod?.projected && bp.index > 0 ? "decrement" : "increment"
        );
    }

    private static calculateWholeNumberCharge(
        options: { remainingPremium: number; totalUnpaidBillingPeriods: number },
        type: "increment" | "decrement"
    ): number {
        const { remainingPremium, totalUnpaidBillingPeriods } = options;
        let chargeValue = remainingPremium / totalUnpaidBillingPeriods;
        if (isWholeNumber(chargeValue)) {
            return chargeValue;
        }
        chargeValue = Math.ceil(chargeValue);
        if (type === "increment") {
            chargeValue += 1;
        } else if (type === "decrement") {
            chargeValue -= 1;
        }

        return chargeValue;
    }

    static getReportAmount({
        bp,
        invoices,
        charges,
    }: {
        bp: BillingPeriod;
        invoices: IAmount;
        charges: IAmount;
    }): [IAmount, { overrideChargedWithReportAmount: boolean }] {
        const { report } = bp;
        const hasAmsInvoices = bp.invoices.length > 0;
        const hasStripeCharges = bp.charges.length > 0;
        const isReportFinalizedOrSubmitted = (report?.isFinalized || report?.isSubmitted) ?? false;
        const hasAmountStillDueInAms = invoices.charged - invoices.paid > 0;
        const stripeAmountCharged = charges.charged;

        if (!report || (bp.isFuture && !isReportFinalizedOrSubmitted) || (hasAmsInvoices && hasStripeCharges)) {
            return [{ paid: 0, charged: 0 }, { overrideChargedWithReportAmount: false }];
        }

        if (
            isReportFinalizedOrSubmitted &&
            !hasAmsInvoices &&
            (!hasStripeCharges ||
                (bp.index === 0 &&
                    bp.schedule.specialPayment?.isEscrowDeposit &&
                    stripeAmountCharged === bp.schedule.specialPayment?.amount))
        ) {
            return [
                {
                    paid: bp.charges.find((pmt) => pmt.amountReceived === bp.report.totalPremium)?.amountReceived || 0,
                    charged: bp.report.totalPremium,
                },
                {
                    overrideChargedWithReportAmount: false,
                },
            ];
        } else if (!hasAmsInvoices && !hasStripeCharges) {
            return [
                {
                    paid: 0,
                    charged: report.totalPremium,
                },
                {
                    overrideChargedWithReportAmount: false,
                },
            ];
        } else if (!hasAmsInvoices && hasStripeCharges) {
            return [
                {
                    paid: bp.charges.find((pmt) => pmt.amountReceived === bp.report.totalPremium)?.amountReceived || 0,
                    charged: report.totalPremium,
                },
                {
                    overrideChargedWithReportAmount: false,
                },
            ];
        } else if (hasAmsInvoices && !hasStripeCharges && report?.hasPremiumModifications && hasAmountStillDueInAms && !bp.isFuture) {
            //
            return [
                {
                    paid: 0,
                    charged: report.totalPremium,
                },
                {
                    overrideChargedWithReportAmount: true,
                },
            ];
        }

        return [{ paid: 0, charged: 0 }, { overrideChargedWithReportAmount: false }];
    }

    static getInvoiceAndChargeAmounts(bp: BillingPeriod): { invoices: IAmount; charges: IAmount } {
        const amsEnteredPaymentAmounts = new Set<number>();
        const invoices = bp.invoices.reduce(
            (acc, inv) => {
                const { paid, charged } = Invoice.calculateAccountingTotals({
                    invoice: inv,
                    billMethod: inv.billMethod || bp.schedule.policy.billMethod,
                });
                amsEnteredPaymentAmounts.add(paid);

                return {
                    paid: acc.paid + paid,
                    charged: acc.charged + charged,
                };
            },
            { paid: 0, charged: 0 } as { paid: number; charged: number }
        );
        const charges = bp.charges.reduce(
            (acc, pi) => {
                if (amsEnteredPaymentAmounts.has(pi.amountReceived)) {
                    return acc;
                }
                const feeAmount = pi.metadata?.[PaymentIntentMetadata.includedFeeAmount]
                    ? parseFloat(pi.metadata[PaymentIntentMetadata.includedFeeAmount] as string) / 100
                    : 0;
                return {
                    paid: acc.paid + (pi.amountReceived >= feeAmount ? pi.amountReceived - feeAmount : pi.amountReceived),
                    charged: invoices.charged > 0 ? 0 : acc.charged + (pi.amount - feeAmount),
                };
            },
            { paid: 0, charged: 0 } as { paid: number; charged: number }
        );
        return { invoices, charges };
    }

    static getAmount(bp: BillingPeriod): IAmount {
        const { invoices, charges } = BillingPeriod.getInvoiceAndChargeAmounts(bp);

        const [report, { overrideChargedWithReportAmount }] = BillingPeriod.getReportAmount({
            bp,
            invoices,
            charges,
        });

        let paid = invoices.paid + charges.paid;
        const reportHasNoPayment = report?.paid === 0;
        let charged = overrideChargedWithReportAmount
            ? report.charged
            : invoices.charged + charges.charged + (reportHasNoPayment ? report.charged : 0);

        if (bp.index === 0 && bp.schedule.isComposite) {
            const escrowAttribute = bp.schedule.policy.attributes.find(
                (a) => a.attributeOptionId === AttributeOption.Id.ESCROW_DEPOSIT_AMOUNT
            );
            if (escrowAttribute) {
                const escrowAmount = escrowAttribute.valueNumber || 0;
                charged -= escrowAmount;
                const escrowPaymentIntent = bp.charges.find(
                    (pi) => pi.metadata?.[PaymentIntentMetadata.paymentType] === PaymentIntentMetadata.PaymentType.escrowDeposit
                );
                if (escrowPaymentIntent) {
                    paid -= escrowAmount;
                } else {
                    const invoiceWithEscrowDeposit = bp.invoices.find((i) => Invoice.includesEscrowDeposit({ invoice: i }));
                    if (invoiceWithEscrowDeposit?.isPaid || escrowPaymentIntent) {
                        paid -= escrowAmount;
                    }
                }
                return {
                    paid,
                    charged,
                };
            }

            const escrowDepositInvoiceTransaction = bp.invoices
                .map((inv) => {
                    return inv.transactions;
                })
                .flat()
                .filter((glt) => glt.description?.toLowerCase() === "escrow deposit");
            const gltRefNumSet = new Set<number>();
            const escrowDepositGeneralLedgerTransaction = bp.invoices
                .map((inv) => {
                    return inv.generalLedgerTransactions;
                })
                .flat()
                .filter((glt) => glt.sourceId === 1 && glt.paid > 0 && glt.description?.toLowerCase() === "escrow deposit")
                .filter((glt) => {
                    if (gltRefNumSet.has(glt.sourceReferenceNumber)) {
                        return false;
                    }
                    gltRefNumSet.add(glt.sourceReferenceNumber);
                    return true;
                });

            if (escrowDepositInvoiceTransaction) {
                charged -= escrowDepositInvoiceTransaction.reduce((acc, tran) => acc + tran.premium, 0) || 0;
            }
            if (escrowDepositGeneralLedgerTransaction) {
                paid -= escrowDepositGeneralLedgerTransaction.reduce((acc, tran) => acc + tran.paid, 0) || 0;
            }
        } else if (
            !bp.invoices.length &&
            !bp.charges.length &&
            !bp.schedule.isComposite &&
            bp.schedule.policy.paymentPlanId !== PaymentPlan.Id.PREMIUM_FINANCE &&
            bp.schedule.policy.paymentPlanId !== PaymentPlan.Id.FULL
        ) {
            charged = BillingPeriod.calculateProjectedCharge(bp);
        }

        return {
            paid,
            charged,
        };
    }

    static getConstructor(
        schedule: PolicyBilling
    ):
        | typeof CompositeBillingPeriod
        | typeof FullPaytBillingPeriod
        | typeof PremiumFinancedPolicyBillingPeriod
        | typeof InstallmentWithDownPaymentBillingPeriod
        | typeof InstallmentBillingPeriod {
        if (Policy.isComposite(schedule.policy)) {
            return CompositeBillingPeriod;
        }
        if (schedule.policy.paymentPlanId === PaymentPlan.Id.FULL) {
            return FullPaytBillingPeriod;
        }
        if (schedule.policy.paymentPlanId === PaymentPlan.Id.PREMIUM_FINANCE) {
            return PremiumFinancedPolicyBillingPeriod;
        }
        if (PaymentPlan.includesDownPayment(schedule.policy.paymentPlanId)) {
            return InstallmentWithDownPaymentBillingPeriod;
        }
        return InstallmentBillingPeriod;
    }

    static from(billingPeriod: BillingPeriod, schedule: PolicyBilling): BillingPeriod {
        const Ctor = BillingPeriod.getConstructor(schedule);

        const bp = new Ctor({
            index: billingPeriod.index,
            schedule,
            startDate: new DateTime(billingPeriod.startDate),
            endDate: new DateTime(billingPeriod.endDate),
            dueDate: new DateTime(billingPeriod.dueDate),
            isPast: billingPeriod.isPast,
            isCurrent: billingPeriod.isCurrent,
            isFuture: billingPeriod.isFuture,
        });
        bp.#paid = billingPeriod.paid;
        bp.#charged = billingPeriod.charged;
        if (billingPeriod.charges?.length) {
            billingPeriod.charges.map((c) => bp.addCharge(c));
        }
        bp.#report = billingPeriod.report;

        bp.invoices = billingPeriod.invoices.map((i) => {
            const inv = new Invoice();
            inv.accountManagerCode = i.accountManagerCode;
            inv.agentCode = i.agentCode;
            inv.billMethod = i.billMethod;
            inv.changedDate = i.changedDate;
            inv.paid = i.paid;
            inv.charged = i.charged;
            inv.dueDate = i.dueDate;
            inv.effectiveDate = i.effectiveDate;
            inv.enteredDate = i.enteredDate;
            inv.generalLedgerTransactions = i.generalLedgerTransactions;
            inv.transactions = i.transactions;
            inv.id = i.id;
            inv.invoiceDate = i.invoiceDate;
            inv.isPaid = i.isPaid;
            inv.number = i.number;
            inv.policyId = i.policyId;
            inv.policyNumber = i.policyNumber;
            return inv;
        });
        return bp;
    }

    id: string;
    index: number;
    startDate: DateTime;
    endDate: DateTime;
    dueDate: DateTime;
    isPast: boolean;
    isCurrent: boolean;
    isFuture: boolean;
    invoices: Invoice[] = [];
    #charges: Charge[] = [];
    totalDaysInPeriod: number;
    #schedule: PolicyBilling;

    constructor(options: BillingPeriod.Options) {
        this.#schedule = options.schedule;
        this.startDate = options.startDate;
        this.endDate = options.endDate;
        this.dueDate = options.dueDate;
        this.isPast = options.isPast;
        this.isCurrent = options.isCurrent;
        this.isFuture = options.isFuture;
        this.totalDaysInPeriod = DateTime.differenceInCalendarDays(this.endDate, this.startDate, { includeEndDate: true });
        this.id = `${this.startDate.format("ISO")}-${this.endDate.format("ISO")}`;
        this.index = options.index;
    }

    get paymentReminderDate(): DateTime {
        return PolicyBillingNotification.getActionDate(PolicyBillingNotification.Action.UPCOMING_PAYMENT_REMINDER, this.dueDate);
    }

    get charges(): readonly Charge[] {
        return this.#charges;
    }

    addCharge(p: Charge): void {
        this.#charges.push(Charge.new(p, this));
    }

    get statementId(): string {
        return `${this.schedule.policy.number}-${this.startDate.format("MMDDYY")}-${this.endDate.format("MMDDYY")}`;
    }

    get schedule(): PolicyBilling {
        return this.#schedule;
    }

    get isLast(): boolean {
        return DateTime.isEqual(this.startDate, this.#schedule.billingPeriods.at(-1).startDate, { excludeTime: true });
    }

    getSmallBalanaceWaivedAmount(): number {
        return this.invoices.reduce((acc, inv) => {
            return acc + Invoice.getSmallBalanaceWaivedAmount(inv);
        }, 0);
    }

    get projected(): boolean {
        const thereAreValidAmsInvoices = this.invoices.reduce((inv, tot) => inv + tot.charged, 0) > 0;
        const thereAreValidStripeCharges = this.charges.length > 0;

        if (thereAreValidAmsInvoices || thereAreValidStripeCharges) {
            return false;
        }

        if (this.hasCharge && this.isPaid) {
            return false;
        }

        return true;
    }

    getTotalPremiumCharge(): number {
        const endoCharges = this.getEndorsements().reduce((acc, endo) => acc + endo.premium, 0);
        return this.charged - endoCharges;
    }

    #calculateEndorsementsAndPremium(): BillingPeriod.Endorsement[] {
        if (!this.invoices) return [];
        const invoiceNumbersForTransaction = new Map<string, number>();
        const transactions = this.invoices
            .map((inv) => {
                inv.transactions.forEach((tran) => {
                    invoiceNumbersForTransaction.set(tran.id, inv.number);
                });
                return inv.transactions;
            })
            .flat();
        return transactions.reduce((acc, transaction) => {
            if (transaction.description?.toLowerCase().includes("endo")) {
                acc.push({
                    id: transaction.id,
                    invoiceNumber: invoiceNumbersForTransaction.get(transaction.id),
                    effectiveDate: new DateTime(transaction.policyTransactionEffectiveDate),
                    description: transaction.description,
                    premium: transaction.premium,
                });
            }
            return acc;
        }, [] as BillingPeriod.Endorsement[]);
    }

    #endorsementsAndPremium: BillingPeriod.Endorsement[] | null = null;
    getEndorsements(): BillingPeriod.Endorsement[] {
        if (this.#endorsementsAndPremium) return this.#endorsementsAndPremium;
        this.#endorsementsAndPremium = this.#calculateEndorsementsAndPremium();
        return this.#endorsementsAndPremium;
    }

    protected abstract _calculatePaidAndCharged(): { paid: number; charged: number };

    #paid = null;
    #charged = null;
    #calculatePaidAndCharged(): { paid: number; charged: number } {
        if (this.#paid !== null && this.#charged !== null) {
            return {
                paid: this.#paid,
                charged: this.#charged,
            };
        }

        const { paid, charged } = this._calculatePaidAndCharged();
        this.#paid = paid;
        this.#charged = charged;
        return {
            paid,
            charged,
        };
    }

    get paid(): number {
        return this.#calculatePaidAndCharged().paid;
    }

    get charged(): number {
        return this.#calculatePaidAndCharged().charged;
    }

    get due(): number {
        return this.charged - this.paid;
    }

    get isFirst(): boolean {
        return DateTime.isEqual(this.startDate, this.#schedule.billingPeriods[0].startDate, { excludeTime: true });
    }

    get isPaid(): boolean {
        return this.paid >= this.charged;
    }

    get isCurrentDue(): boolean {
        return this.#schedule.getCurrentDue().id === this.id;
    }

    get hasCharge(): boolean {
        return this.charged > 0;
    }

    get hasPaidSome(): boolean {
        return this.paid > 0;
    }

    get hasNonProjectedBalanceDue(): boolean {
        if (this.projected) return false;
        return this.hasBalanceDue;
    }

    get hasBalanceDue(): boolean {
        if (this.charged <= 0 && this.report?.totalPremium > 0) {
            return this.paid < this.report.totalPremium;
        }
        return this.paid < this.charged;
    }

    get isNonBilled(): boolean {
        const isPastDueDate = DateTime.today.removeTimezoneOffset().isAfter(this.dueDate);
        if (isPastDueDate && this.charged === 0) return true;
        return false;
    }

    get isOverdue(): boolean {
        const isPastDueDate = DateTime.today.removeTimezoneOffset().isAfter(this.dueDate);
        if (isPastDueDate && this.paid < this.charged) return true;
        return false;
    }

    get totalDue(): number {
        const total = this.charged - this.paid;
        if (total < 0) return 0;
        return total;
    }

    get percentagePaid(): number {
        if (this.charged === 0) return 0;
        return NumberFormatter.round((this.paid / this.charged) * 100, 2);
    }

    #report: Policy.IReport | null = null;
    get report(): Policy.IReport | null {
        if (this.#report) return this.#report;
        if (!this.#schedule.isComposite) return null;
        const report =
            this.#schedule.policy.reports?.find((r) => {
                const reportDate = new DateTime(r.year, r.month - 1, 1);
                if (
                    this.isLast &&
                    reportDate.getFullYear() === this.endDate.getFullYear() &&
                    reportDate.getMonth() === this.endDate.getMonth()
                ) {
                    return r;
                } else if (reportDate.last().isEqual(this.endDate, { excludeTime: true })) {
                    return r;
                }
                return null;
            }) || null;
        this.#report = report;
        return report;
    }

    getDue(): {
        projected: boolean;
        amount: number;
    } {
        return {
            projected: this.projected,
            amount: this.totalDue,
        };
    }

    getCharged(): {
        projected: boolean;
        amount: number;
    } {
        return {
            projected: this.projected,
            amount: this.charged,
        };
    }

    calculateIsFullMonth(): boolean {
        if (DateTime.addMonths(this.startDate, 1).subtractDays(1).isEqual(this.endDate, { excludeTime: true })) {
            return true;
        }
        return false;
    }

    getPrevious(): BillingPeriod | null {
        if (this.isFirst) return null;

        const prevBillingPeriod = this.#schedule.billingPeriods.find((bp) => {
            return bp.index === this.index - 1;
        });
        if (!prevBillingPeriod) return null;
        return prevBillingPeriod;
    }

    getAllPrevious(): BillingPeriod[] {
        if (this.isFirst) return null;

        return this.#schedule.billingPeriods.filter((bp) => {
            return bp.index < this.index;
        });
    }

    getAllPreviousWithCharges(): BillingPeriod[] {
        if (this.isFirst) return [];

        return this.getAllPrevious().filter((bp) => {
            return bp.charged > 0 && !bp.projected;
        });
    }

    getNext(): BillingPeriod | null {
        if (this.isLast) return null;

        const nextBillingPeriod = this.#schedule.billingPeriods.find((bp) => {
            return bp.index === this.index + 1;
        });
        if (!nextBillingPeriod) return null;
        return nextBillingPeriod;
    }

    getMergedChargesAndInvoices(): BillingPeriod.MergedInvoiceAndCharge[] {
        const map = new Map<string, { invoice: Invoice | null; charge: Charge | null }[]>();

        const sumOfAllInvoices = this.invoices.reduce((acc, inv) => acc + inv.paid, 0);
        const sumOfAllCharges = this.charges.reduce((acc, c) => acc + c.amount, 0);

        if (sumOfAllInvoices === sumOfAllCharges && this.charges.length < this.invoices.length && this.charges.length === 1) {
            return this.invoices.map((i) => {
                return this.#mergeInvoiceAndCharge({ invoice: i, charge: i.paid > 0 ? this.charges[0] : null });
            });
        }
        if (sumOfAllInvoices === sumOfAllCharges && this.charges.length === this.invoices.length) {
            const invoices = this.invoices.sort((a, b) => b.charged - a.charged);
            const charges = [...this.charges].sort((a, b) => b.amount - a.amount);
            return invoices
                .map((inv, index) => {
                    return this.#mergeInvoiceAndCharge({
                        invoice: inv,
                        charge: charges[index],
                    });
                })
                .sort((a, b) => parseInt(a.invoiceNumber) - parseInt(b.invoiceNumber));
        }
        if (sumOfAllInvoices === sumOfAllCharges && this.charges.length < this.invoices.length) {
            const invoices = this.invoices
                .sort((a, b) => b.charged - a.charged)
                .map((i) => {
                    return {
                        invoice: i,
                        charge: null as Charge | null,
                    };
                });
            let charges = [...this.charges]
                .sort((a, b) => b.amount - a.amount)
                .map((c) => {
                    return {
                        charge: c,
                        amountRemaining: c.amount,
                    };
                });

            for (const inv of invoices) {
                if (inv.charge) continue;

                if (charges.length === 1) {
                    inv.charge = charges[0].charge;
                    continue;
                }

                const matchChargeAmount = charges.find((c) => c.charge.amount === inv.invoice.charged);
                if (matchChargeAmount) {
                    charges = charges.filter((c) => c.charge.id !== matchChargeAmount.charge.id);
                    inv.charge = matchChargeAmount.charge;
                    continue;
                }
                const partialChargeAmount = charges.find((c) => inv.invoice.charged <= c.amountRemaining);
                if (partialChargeAmount) {
                    inv.charge = partialChargeAmount.charge;
                    partialChargeAmount.amountRemaining -= inv.invoice.charged;
                    if (partialChargeAmount.amountRemaining <= 0) {
                        charges = charges.filter((c) => c.charge.id !== partialChargeAmount.charge.id);
                    }
                    continue;
                }
            }

            return invoices
                .map((inv) => {
                    return this.#mergeInvoiceAndCharge({
                        invoice: inv.invoice,
                        charge: inv.charge,
                    });
                })
                .sort((a, b) => parseInt(a.invoiceNumber) - parseInt(b.invoiceNumber));
        }

        this.invoices.forEach((i) => {
            const key = `${i.charged}_${new DateTime(i.dueDate || i.effectiveDate).format("YYYY-MM-DD")}`;
            const existing = map.get(key);
            if (!existing) {
                map.set(key, [{ invoice: i, charge: null }]);
            } else {
                existing.push({ invoice: i, charge: null });
                existing.sort((a, b) => {
                    return a.invoice.number - b.invoice.number;
                });
                map.set(key, existing);
            }
        });
        const sortedCharges = this.charges.map((c) => c).sort((a, b) => a.createdDate.getTime() - b.createdDate.getTime());
        sortedCharges.forEach((c) => {
            const key = `${c.amount}_${new DateTime(c.dueDate).format("YYYY-MM-DD")}`;
            const existing = map.get(key);
            if (!existing) {
                map.set(key, [{ invoice: null, charge: c }]);
            } else {
                map.set(
                    key,
                    existing.map((e) => {
                        if (!e.charge) {
                            e.charge = c;
                        }
                        return e;
                    })
                );
            }
        });

        return Array.from(map.values())
            .flat()
            .map((v) => {
                return this.#mergeInvoiceAndCharge(v);
            });
    }

    #mergeInvoiceAndCharge(v: { invoice: Invoice | null; charge: Charge | null }): BillingPeriod.MergedInvoiceAndCharge {
        return {
            id: v.invoice?.id || v.charge?.id,
            chargeId: v.charge?.id,
            invoiceNumber: v.invoice?.number.toString() || v.charge?.id.slice(v.charge.id.length - 6).toUpperCase(),
            paymentNumber: v.invoice?.isPaid
                ? v.invoice?.number.toString()
                : v.charge?.id.slice(v.charge.id.length - 6).toUpperCase() || null,
            charged: v.invoice?.charged || v.charge?.amount,
            paymentTypeDescription: this.#getMergedChargeAndInvoicePaymentTypeDescription(v.invoice, v.charge),
            paid: v.invoice?.paid || v.charge?.amountReceived,
            dueDate: v.invoice?.dueDate ? new DateTime(v.invoice?.dueDate) : v.charge?.dueDate,
            effectiveDate: v.invoice?.effectiveDate ? new DateTime(v.invoice?.effectiveDate) : v.charge?.createdDate,
            reconciled: v.invoice?.isPaid ?? false,
            isAmsInvoice: Boolean(v.invoice?.id),
            isStripePaymentEnteredInAms: v.invoice?.paid >= v.charge?.amountReceived,
        };
    }

    #getMergedChargeAndInvoicePaymentTypeDescription(invoice: Invoice | null, charge: Charge | null): string {
        if ((!invoice && charge) || charge?.amountReceived === invoice?.paid) {
            if (charge.paymentMethod === "us_bank_account") return "ACH";
            if (charge.paymentMethod === "card") return "Card";
            if (charge.paymentMethod) {
                const type = charge.paymentMethod.split("_").join(" ");
                return type.charAt(0).toUpperCase() + type.slice(1);
            }
            return "Unknown";
        }
        const paymentTypes = new Set<string>();
        if (invoice.transactions.some((t) => t.description?.toLowerCase().includes("eft"))) {
            paymentTypes.add("EFT");
        }
        if (invoice.transactions.some((t) => t.description?.toLowerCase().includes("check"))) {
            paymentTypes.add("Check");
        }
        if (paymentTypes.size > 0) {
            return Array.from(paymentTypes).join(" / ");
        }

        return "";
    }

    json(): BillingPeriod {
        return {
            ...this,
            paid: this.paid,
            charged: this.charged,
            report: this.#report,
            charges: this.#charges.map((p) => p.json()),
        };
    }
}

export namespace BillingPeriod {
    export interface Endorsement {
        id: string;
        invoiceNumber: number;
        effectiveDate: DateTime;
        description: string;
        premium: number;
    }

    export interface Options {
        index: number;
        schedule: PolicyBilling;
        startDate: DateTime;
        endDate: DateTime;
        dueDate: DateTime;
        isPast: boolean;
        isCurrent: boolean;
        isFuture: boolean;
    }

    export interface MergedInvoiceAndCharge {
        id: string;
        chargeId?: string;
        invoiceNumber: string;
        paymentNumber: string | null;
        charged: number;
        paymentTypeDescription: string;
        paid: number;
        dueDate: DateTime;
        effectiveDate: DateTime;
        reconciled: boolean;
        isAmsInvoice: boolean;
        isStripePaymentEnteredInAms: boolean;
    }
}

export class PremiumFinancedPolicyBillingPeriod extends BillingPeriod {
    protected _calculatePaidAndCharged(): { paid: number; charged: number } {
        if (!this.invoices.length && !this.charges.length) {
            return {
                paid: 0,
                charged: this.schedule.policy.totalCost,
            };
        }

        return BillingPeriod.getAmount(this);
    }
}

export class FullPaytBillingPeriod extends BillingPeriod {
    protected _calculatePaidAndCharged(): { paid: number; charged: number } {
        if (!this.invoices.length && !this.charges.length) {
            return {
                paid: 0,
                charged: this.schedule.policy.totalCost,
            };
        }

        return BillingPeriod.getAmount(this);
    }
}

export class InstallmentBillingPeriod extends BillingPeriod {
    protected _calculatePaidAndCharged(): { paid: number; charged: number } {
        return BillingPeriod.getAmount(this);
    }
}

export class InstallmentWithDownPaymentBillingPeriod extends BillingPeriod {
    protected _calculatePaidAndCharged(): { paid: number; charged: number } {
        if (!this.invoices.length && !this.charges.length) {
            if (this.index === 0) {
                return {
                    paid: 0,
                    charged: this.schedule.specialPayment.amount,
                };
            }
        }

        return BillingPeriod.getAmount(this);
    }

    get projected(): boolean {
        if (this.index === 0 && this.schedule.specialPayment.isDownPayment) return false;
        return super.projected;
    }
}

export class CompositeBillingPeriod extends BillingPeriod {
    get projected(): boolean {
        const reportIsValid = (this.report && (this.report.isFinalized || this.report.isSubmitted)) ?? false;
        if (reportIsValid) {
            return false;
        }

        if (this.report?.hasPremiumModifications) {
            return true;
        }

        if (this.isFirst) {
            const validInvoices = this.invoices.filter((i) => i.charged > 0);
            const nonEscrowInvoicesWithCharge = validInvoices.filter((i) => {
                if (i.id === this.schedule.specialPayment?.invoiceId?.toString()) {
                    return false;
                }
                return true;
            });
            const nonEscrowStripePayments = this.charges.filter(
                (pi) => pi.metadata?.[PaymentIntentMetadata.paymentType] !== PaymentIntentMetadata.PaymentType.escrowDeposit
            );
            const hasNonEscrowInvoices = nonEscrowInvoicesWithCharge.length > 0;
            const hasNonEscrowCharges = nonEscrowStripePayments?.length > 0;
            if (!hasNonEscrowCharges && !hasNonEscrowInvoices) {
                return true;
            }
        }

        return super.projected;
    }

    protected _calculatePaidAndCharged(): { paid: number; charged: number } {
        const amount = BillingPeriod.getAmount(this);
        if (amount.charged > 0) {
            return {
                paid: amount.paid,
                charged: amount.charged,
            };
        }

        const costPerDay = this.schedule.projectedCostPerDay || this.schedule.policy.fullTermPremium / this.schedule.totalDaysInPolicy;
        const totalDaysInPeriod = this.totalDaysInPeriod;

        if (this.isPast && (this.report?.isFinalized || this.report?.isSubmitted)) {
            return {
                paid: amount.paid,
                charged: this.report.totalPremium,
            };
        }

        const reportIsValid = (this.report && (this.report.isFinalized || this.report.isSubmitted)) ?? false;
        // A valid zero-dollar report is accounting data, not a missing amount to project.
        if (reportIsValid && !this.invoices.length && !this.charges.length) {
            return amount;
        }

        return {
            paid: amount.paid,
            charged: costPerDay * totalDaysInPeriod,
        };
    }
}
